import { URL_PARSE_OPTIONS, URL_STRINGIFY_OPTIONS, useAppConfig } from 'application/configuration'
import { Description } from 'features/car-details-card/components/Description'
import { DetailsCardTabs } from 'features/car-details-card/components/DetailsCardTabs'
import { View360 } from 'features/car-details-card/components/View360'
import { prepareCarData } from 'features/car/normalization'
import { reverseString } from 'features/car/normalization/string'
import { Form } from 'features/form/components'
import { useSingleCarSmartContractAPI } from 'features/smart-contract/detail/useSingleCarSmartContractAPI'
import { appendUrl } from 'lib/append-url'
import { createMediaStyles } from 'lib/styled-helpers'
import { parseUrl } from 'query-string'
import React, { FC, lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react'
import { FormContextProvider } from 'stages/form'
import styled, { useTheme } from 'styled-components'
import { Button } from 'ui/button'
import { Cell, Container, Row } from 'ui/grid'
import { Icon } from 'ui/icon'
import { ModalWindow } from 'ui/modal-window'
import { ModalWindowContent } from 'ui/modal-window-content'
import { SkeletonBlock } from 'ui/skeleton-block'
import { Tab, Tabs } from 'ui/tabs'

import { useCollapsedCars } from '../../../api/collapsed-cars'
import { getProjectName } from '../../../lib/get-project-name'
import { getFromStorage, setToStorage } from '../../../lib/storage'
import { useLoopCallback } from '../../../lib/use-loop-callback'
import { useCarDetailsCardConfig } from '../../../stages/car-details'
import { EcomIconsId } from '../../../ui/icon/types'
import { Label } from '../../../ui/label'
import { PreviewSlider } from '../../../ui/preview-slider/PreviewSlider'
import { SliderSlick } from '../../../ui/slider/SliderSlick'
import { FullInfoAboutCar } from './FullInfoAboutCar'
import { Guarantees } from './Guarantees'
import { Iframe } from './Iframe'
import { MoreInfoContent } from './ModalInfoContent'
import { ImagesPreview, VideosPreview } from './Preview'
import GlobalStyles from './style'

const MiniSmartContract = lazy(() => import('features/mini-smart-contract/components'))

export const CarDetailsCard: FC<{}> = () => {
  const {
    baseCarModel,
    carViewSettings,
    linkAboutModel,
    backLink,
    smartContractEnabled,
    showIMV,
    showInspections,
    getCollapsedCardURL,
    tabs,
    form,
    hasCollapsedCars,
    dealerDescription,
    dealerDescriptionTitle,
    similarCarsIndicator,
    i18n,
    formRequestInformation,
    formReserved,
    formContactManager,
    buttonFirst,
    buttonSecond,
    showColorsInDescriptionOnly,
    gallery
  } = useCarDetailsCardConfig()

  const [modalVisible, setModalVisible] = useState<boolean>(false)
  const [largeView, setLargeView] = useState<boolean>(false)
  const [large360View, setLarge360View] = useState<boolean>(false)
  const [modalInfoVisible, setModalInfoVisible] = useState<boolean>(false)
  const [modalMoreVisible, setModalMoreVisible] = useState<boolean>(false)
  const [modalReserveVisible, setModalReserveVisible] = useState<boolean>(false)
  const [sent, setSent] = useState<boolean>(false)

  const [activeImage, setActiveImage] = useState<null | number>(null)
  const [activeVideo, setActiveVideo] = useState<null | number>(null)

  const appTheme = useTheme()
  const carData = useMemo(() => prepareCarData(baseCarModel, carViewSettings, appTheme), [
    appTheme,
    baseCarModel,
    carViewSettings,
    tabs
  ])
  const collapsedCars = useCollapsedCars(hasCollapsedCars, getCollapsedCardURL, carData)
  const smartContractConfig = useAppConfig().smartContract
  const CTAButtonRef = useRef<HTMLButtonElement | null>(null)
  const i18nApp = useAppConfig().i18n
  const {
    images,
    thumbImages,
    videos,
    marketingColors,
    labels,
    additionalEquipmentCost,
    priceWithDiscounts,
    id,
    creditDiscount,
    tradeInDiscount,
    insuranceDiscount
  } = carData
  const { detailBackground } = useCarDetailsCardConfig()
  const { init, state, failed, formsWillBeReplaced } = useSingleCarSmartContractAPI()
  const backLinkUrl = useMemo(() => {
    return appendUrl({
      url: backLink.link.url,
      parseOptions: URL_PARSE_OPTIONS,
      stringifyOptions: URL_STRINGIFY_OPTIONS,
      data: parseUrl(window.location.href, URL_PARSE_OPTIONS).query
    })
  }, [backLink.link.url])
  const [showContactManagerPopup, setShowContactManagerPopup] = useState(false)

  const [creditDiscountSwitch, setCreditDiscountSwitch] = useState(false)
  const [tradeInDiscountSwitch, setTradeInDiscountSwitch] = useState(false)
  const [insuranceDiscountSwitch, setInsuranceDiscountSwitch] = useState(false)

  useEffect(() => {
    let discountSettings = getFromStorage('discountSettings') ? getFromStorage('discountSettings') : {}
    if (discountSettings && discountSettings[id]) {
      setCreditDiscountSwitch(discountSettings[id].creditDiscount)
      setTradeInDiscountSwitch(discountSettings[id].tradeInDiscount)
      setInsuranceDiscountSwitch(discountSettings[id].insuranceDiscount)
    } else {
      discountSettings[id] = {
        tradeInDiscount: false,
        creditDiscount: false,
        insuranceDiscount: false
      }
      setToStorage('discountSettings', discountSettings)
    }
  }, [])
  useEffect(() => {
    collapsedCars && init({ ...carData, collapsedCars })
    document.dispatchEvent(new CustomEvent('kodix:ecom:item_view', { detail: { carId: carData } }))
  }, [collapsedCars])

  useEffect(() => {
    const mainContentNode = document.querySelector<HTMLElement>('.main-content')
    if (mainContentNode === null) return
    large360View || largeView ? (mainContentNode.style.zIndex = 'auto') : (mainContentNode.style.zIndex = '2')
  }, [large360View, largeView])

  const shouldShowMiniSmartContract = useMemo(() => {
    if (!carData.available) return false
    if (!smartContractEnabled) return false
    if (failed) return false
    if (!state) return null
    if (state.settings.dealerships.length === 0) return true
    return !!state?.settings.dealerships.find((dealer) => dealer.cars.some((car) => car.status === 'available'))
  }, [smartContractEnabled, state, failed])
  let formatPrice = String(priceWithDiscounts).replace(/[^0-9]/g, '')
  let icons = ['auto-avito' as EcomIconsId, 'interior-avito' as EcomIconsId, '360-avito' as EcomIconsId]
  const slickSliderVideosRef = useRef<any>(null)
  const setSlide = (index: number) => {
    if (slickSliderVideosRef?.current?.slickGoTo) {
      slickSliderVideosRef?.current.slickGoTo(index)
    }
  }

  const containerWrapperRef = useRef<HTMLDivElement>(null)

  const handleButtonVinReportClick = () => {
    document.dispatchEvent(new CustomEvent('kodix:ecom:vinReportButtonClicked', {}))
  }

  return (
    <Inner bg={detailBackground}>
      <Container>
        <ModalWindow onCloseRequest={() => setModalMoreVisible(false)} visible={modalMoreVisible}>
          <MoreInfoContent />
        </ModalWindow>
        {form && (
          <ModalWindow onCloseRequest={() => setModalVisible(false)} visible={modalVisible}>
            <FormContextProvider value={form}>
              <Form skipRender={formsWillBeReplaced} sent={sent} setSent={setSent} />
            </FormContextProvider>
          </ModalWindow>
        )}
        {baseCarModel.interior360 && (
          <ModalWindowContent onCloseRequest={() => setLargeView(false)} visible={largeView} mediumWidth={true}>
            <Iframe src={baseCarModel.interior360} inPopup={true} />
          </ModalWindowContent>
        )}
        {baseCarModel.exterior360 && baseCarModel.exterior360?.folder !== '' && (
          <ModalWindowContent onCloseRequest={() => setLarge360View(false)} visible={large360View} mediumWidth={true}>
            <View360
              id={reverseString(baseCarModel.exterior360.id)}
              folder={baseCarModel.exterior360.folder}
              filenameX={baseCarModel.exterior360.filenameX}
              amountX={baseCarModel.exterior360.amountX}
              inPopup={true}
            />
          </ModalWindowContent>
        )}
        {formRequestInformation && getProjectName() === 'avito' && (
          <ModalWindow
            onCloseRequest={() => {
              setModalInfoVisible(false)
              setSent(false)
            }}
            visible={modalInfoVisible}
          >
            <FormContextProvider value={formRequestInformation}>
              <Form
                skipRender={false}
                sent={sent}
                setSent={setSent}
                idCar={id}
                creditDiscount={creditDiscount}
                tradeInDiscount={tradeInDiscount}
                insuranceDiscount={insuranceDiscount}
                textPosition={getProjectName() === 'avito' ? 'left' : ''}
                formDataPayload={getProjectName() === 'avito' ? { title: formRequestInformation.title } : null}
              />
            </FormContextProvider>
          </ModalWindow>
        )}
        {formReserved && getProjectName() === 'avito' && (
          <ModalWindow onCloseRequest={() => setModalReserveVisible(false)} visible={modalReserveVisible}>
            <FormContextProvider value={formReserved}>
              <Form
                skipRender={false}
                sent={sent}
                setSent={setSent}
                idCar={id}
                creditDiscount={creditDiscount}
                tradeInDiscount={tradeInDiscount}
                textPosition={getProjectName() === 'avito' ? 'left' : ''}
                insuranceDiscount={insuranceDiscount}
                formDataPayload={getProjectName() === 'avito' ? { title: formReserved.title } : null}
              />
            </FormContextProvider>
          </ModalWindow>
        )}
        {formContactManager && getProjectName() === 'avito' && (
          <ModalWindow onCloseRequest={() => setShowContactManagerPopup(false)} visible={showContactManagerPopup}>
            <FormContextProvider value={formContactManager}>
              <Form
                sent={sent}
                setSent={setSent}
                textPosition={getProjectName() === 'avito' ? 'left' : ''}
                formDataPayload={getProjectName() === 'avito' ? { title: formContactManager.title } : null}
              />
            </FormContextProvider>
          </ModalWindow>
        )}
        <BackLinkWrapper>
          <BackLink href={backLinkUrl}>
            <BackLinkIconWrapper>
              {getProjectName() !== 'generic_v2' && <Icon name={backLink.icon} size={0.7} />}
              {getProjectName() === 'generic_v2' && <Icon name={'zzzzzz-arrow-left'} size={0.6} />}
            </BackLinkIconWrapper>
            <span>{backLink.link.value}</span>
          </BackLink>
        </BackLinkWrapper>
        <Grid>
          <GalleryUnit isTab={baseCarModel.exterior360 || baseCarModel.exterior360 || videos.length > 0}>
            {getProjectName() === 'haval' &&
            (baseCarModel.interior360 || baseCarModel.exterior360 || videos.length > 0) ? (
              <TabsUnit sliderTabs={true}>
                <Tabs
                  customClass={true}
                  arrName={icons}
                  carData={carData}
                  setSlide={setSlide}
                  slickSliderVideosRef={slickSliderVideosRef}
                  containerWrapperRef={containerWrapperRef}
                >
                  <Tab>
                    <FadeOut>
                      <SliderWrapper>
                        <SliderSlick
                          images={[...images]}
                          isInPopup={false}
                          onSlideChange={setActiveImage}
                          settingsSlider={gallery}
                        />
                        <>
                          {carViewSettings.isShowLabel && (
                            <LabelsWrapper>
                              {labels &&
                                labels.map((label, index) => {
                                  return (
                                    <SliderLabelWrapper key={index}>
                                      <Label {...label} />
                                    </SliderLabelWrapper>
                                  )
                                })}
                            </LabelsWrapper>
                          )}

                          <ColorsWrapper showColorsInDescriptionOnly={showColorsInDescriptionOnly}>
                            {marketingColors?.map((color, index) => (
                              <ColorWrapper key={index}>
                                <SliderColor color={color?.colorPrimary} secondaryColor={color?.colorSecondary} />
                                <ColorInner>
                                  <ColorHeading>{color?.heading}</ColorHeading>
                                  <ColorName>{color?.colorName}</ColorName>
                                </ColorInner>
                              </ColorWrapper>
                            ))}
                          </ColorsWrapper>
                          <WrapIcons>
                            {baseCarModel.reportVIN && baseCarModel.reportVIN.link && baseCarModel.reportVIN.text && (
                              <div onClick={() => handleButtonVinReportClick()}>
                                <Button
                                  link={baseCarModel.reportVIN.link}
                                  customSize={'32px'}
                                  borderRadius={'6px'}
                                  targetBlank={'_blank'}
                                >
                                  <ButtonText>{baseCarModel.reportVIN.text}</ButtonText>
                                </Button>
                              </div>
                            )}
                            {videos && videos.length && getProjectName() !== 'haval' ? (
                              <VideoButton onClick={() => setActiveVideo(0)}>
                                <Icon name="video-play2" size={2} />
                              </VideoButton>
                            ) : null}
                          </WrapIcons>
                        </>
                      </SliderWrapper>
                    </FadeOut>
                  </Tab>
                  {baseCarModel.interior360 && (
                    <Tab>
                      <FadeOut>
                        <Iframe src={baseCarModel.interior360} />
                        <LargeViewIcon onClick={() => setLargeView(true)}>
                          <Icon name={'search-avito'} size={1.7} color={'white'} />
                        </LargeViewIcon>
                      </FadeOut>
                    </Tab>
                  )}
                  {baseCarModel.exterior360 && baseCarModel.exterior360?.folder !== '' && (
                    <Tab>
                      <FadeOut>
                        <View360
                          id={baseCarModel.exterior360.id}
                          folder={baseCarModel.exterior360.folder}
                          filenameX={baseCarModel.exterior360.filenameX}
                          amountX={baseCarModel.exterior360.amountX}
                        />
                        <LargeViewIcon
                          onClick={() => {
                            setLarge360View(true)
                            window.CI360.update(null, true)
                          }}
                        >
                          <Icon name={'search-avito'} size={1.7} color={'black'} />
                        </LargeViewIcon>
                      </FadeOut>
                    </Tab>
                  )}
                </Tabs>
              </TabsUnit>
            ) : (
              <>
                <SliderWrapper>
                  <SliderSlick
                    images={[...images]}
                    isInPopup={false}
                    onSlideChange={setActiveImage}
                    settingsSlider={gallery}
                  />
                  <>
                    {carViewSettings.isShowLabel && (
                      <LabelsWrapper>
                        {labels &&
                          labels.map((label, index) => {
                            return (
                              <SliderLabelWrapper key={index}>
                                <Label {...label} />
                              </SliderLabelWrapper>
                            )
                          })}
                      </LabelsWrapper>
                    )}
                    <ColorsWrapper showColorsInDescriptionOnly={showColorsInDescriptionOnly}>
                      {marketingColors?.map((color, index) => (
                        <ColorWrapper key={index}>
                          {getProjectName() !== 'generic_v2' && (
                            <SliderColor color={color?.colorPrimary} secondaryColor={color?.colorSecondary} />
                          )}
                          <div>
                            <ColorHeading>{color?.heading}</ColorHeading>
                            <ColorInner>
                              {getProjectName() === 'generic_v2' && (
                                <SliderColor color={color?.colorPrimary} secondaryColor={color?.colorSecondary} />
                              )}
                              <ColorName>{color?.colorName}</ColorName>
                            </ColorInner>
                          </div>
                        </ColorWrapper>
                      ))}
                    </ColorsWrapper>
                    <WrapIcons>
                      {baseCarModel.reportVIN && baseCarModel.reportVIN.link && baseCarModel.reportVIN.text && (
                        <InteractiveButton onClick={() => handleButtonVinReportClick()}>
                          <Button
                            link={baseCarModel.reportVIN.link}
                            customSize={'32px'}
                            borderRadius={'6px'}
                            targetBlank={'_blank'}
                            textSize={'9px'}
                          >
                            <ButtonText>{baseCarModel.reportVIN.text}</ButtonText>
                          </Button>
                        </InteractiveButton>
                      )}
                      {getProjectName() !== 'haval' && !!videos?.length ? (
                        <VideoButton onClick={() => setActiveVideo(0)}>
                          {getProjectName() !== 'generic_v2' && <Icon name="video-play2" size={2} />}
                          {getProjectName() === 'generic_v2' && <Icon name="video3" size={0.7} />}
                        </VideoButton>
                      ) : null}
                    </WrapIcons>
                  </>
                </SliderWrapper>
              </>
            )}
            {activeImage !== null && (
              <ImagesPreview activeSlide={activeImage} onSlideChange={setActiveImage} images={images} />
            )}

            {activeVideo !== null && (
              <VideosPreview
                activeSlide={activeVideo}
                onSlideChange={setActiveVideo}
                videos={videos}
                setSlide={setSlide}
                slickSliderVideosRef={slickSliderVideosRef}
              />
            )}
          </GalleryUnit>
          <TabsUnit>
            {dealerDescription && (
              <Wrapper>
                <Row alignItems={'center'} className="margin">
                  <Cell>{dealerDescriptionTitle && <Title>{dealerDescriptionTitle}</Title>}</Cell>
                  <Cell>
                    <Text dangerouslySetInnerHTML={{ __html: dealerDescription }} />
                  </Cell>
                </Row>
              </Wrapper>
            )}

            {getProjectName() === 'avito' && (
              <>
                {showIMV && (
                  <SimilarCars>
                    <SimilarCarsInner>
                      <Price>
                        {parseInt(formatPrice).toLocaleString()}{' '}
                        {i18nApp.currencyCode === 'RUB' ? '₽' : i18nApp.currencyCode}
                        &nbsp;&mdash; {i18n.priceText}
                      </Price>
                      <SimilarCarsSubtitle>{i18n.similarCarsText}</SimilarCarsSubtitle>

                      <CostIndicator>
                        {similarCarsIndicator?.map((item) => (
                          <CostIndicatorItem className={item.active ? 'active' : ''} color={item.color}>
                            {item.text}
                          </CostIndicatorItem>
                        ))}
                      </CostIndicator>
                    </SimilarCarsInner>
                  </SimilarCars>
                )}
                <Guarantees id={id} onButtonCLick={() => setModalMoreVisible(true)} />
                {showInspections === true && (
                  <FullInfoAboutCar id={id} onCTAButtonCLick={() => setModalInfoVisible(true)} />
                )}
              </>
            )}

            <DetailsCardTabs additionalEquipmentCost={additionalEquipmentCost} />
          </TabsUnit>
          <DescriptionUnit>
            <DescriptionContainer>
              <DescriptionContainerInner>
                <Description
                  forSmartContract={shouldShowMiniSmartContract !== false}
                  carData={carData}
                  CTAButtonRef={CTAButtonRef}
                  onCTAButtonCLick={() =>
                    getProjectName() === 'avito' ? setModalReserveVisible(true) : setModalVisible(true)
                  }
                  tradeInDiscountSwitch={tradeInDiscountSwitch}
                  setTradeInDiscountSwitch={setTradeInDiscountSwitch}
                  creditDiscountSwitch={creditDiscountSwitch}
                  setCreditDiscountSwitch={setCreditDiscountSwitch}
                  insuranceDiscountSwitch={insuranceDiscountSwitch}
                  setInsuranceDiscountSwitch={setInsuranceDiscountSwitch}
                  setShowContactManagerPopup={setShowContactManagerPopup}
                />
                {shouldShowMiniSmartContract === null && (
                  <SmartContractWrapper>
                    <SkeletonBlock height={appTheme.spacing[40]} />
                  </SmartContractWrapper>
                )}
                {shouldShowMiniSmartContract === true && (
                  <SmartContractWrapper>
                    <Suspense fallback={<SkeletonBlock height={appTheme.spacing[40]} />}>
                      <MiniSmartContract
                        carData={carData}
                        config={smartContractConfig}
                        carViewSettings={carViewSettings}
                      />
                    </Suspense>
                  </SmartContractWrapper>
                )}
              </DescriptionContainerInner>
              {(buttonFirst || buttonSecond) && (
                <CreditLeasingButton>
                  {buttonFirst && (
                    <div style={{ marginBottom: appTheme.spacing[2] }}>
                      <Button
                        size={getProjectName() === 'volkswagen' ? 'small' : 'medium'}
                        link={buttonFirst?.link}
                        block={true}
                        customSize={getProjectName() === 'ora' ? '43px' : null}
                      >
                        {buttonFirst?.text}
                      </Button>
                    </div>
                  )}
                  {buttonSecond && (
                    <Button
                      size={getProjectName() === 'volkswagen' ? 'small' : 'medium'}
                      link={buttonSecond.link}
                      block={true}
                      customSize={getProjectName() === 'ora' ? '43px' : null}
                    >
                      {buttonSecond.text}
                    </Button>
                  )}
                </CreditLeasingButton>
              )}
            </DescriptionContainer>
          </DescriptionUnit>
        </Grid>
        {linkAboutModel && (
          <ButtonContainer>
            <Button
              size={getProjectName() === 'volkswagen' ? 'small' : 'big'}
              link={linkAboutModel.link}
              secondary
              customSize={getProjectName() === 'ora' ? '43px' : null}
            >
              {linkAboutModel.text}
            </Button>
          </ButtonContainer>
        )}
      </Container>
      {getProjectName() === 'haval' && <GlobalStyles />}
    </Inner>
  )
}

const FadeOut = styled.div`
  opacity: 0;
  animation: fadeIn ease-in 1;
  animation-fill-mode: forwards;
  animation-duration: 0.2s;
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`

const SmartContractWrapper = styled.div`
  margin-top: ${({ theme }) => theme.spacing[4]};
`

const BackLinkWrapper = styled.div`
  display: flex;
`

const BackLink = styled.a`
  display: flex;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing[9]};
  color: ${({ theme }) => {
    if (getProjectName() === 'tank') return theme.palette.grey[500]
    if (getProjectName() === 'generic_v2') return theme.palette.grey[100]
    return theme.palette.grey[900]
  }};
  text-decoration: none;
  ${({ theme }) => theme.textStyles.btn};
  text-transform: ${() => {
    if (getProjectName() === 'generic_v2') return 'none'
    return 'uppercase'
  }};
  font-weight: ${() => {
    if (getProjectName() === 'generic_v2') return '400'
    return
  }};
`

const BackLinkIconWrapper = styled.div`
  display: flex;
  margin-right: ${({ theme }) => theme.spacing[1]};
  color: ${({ theme }) => {
    if (getProjectName() === 'tank') return theme.palette.grey[500]
    if (getProjectName() === 'ora') return theme.palette.grey[900]
    if (getProjectName() === 'generic_v2') return theme.palette.grey[100]
    return theme.palette.brand.primary
  }};
`

const DescriptionContainer = styled.div`
  position: sticky;
  top: 0;
`

const DescriptionContainerInner = styled.div`
  background: ${({ theme }) => {
    if (getProjectName() === 'tank') return 'transparent'
    if (getProjectName() === 'sollers') return `rgba(41, 37, 35, 0.1)`
    if (getProjectName() === 'generic_v2') return theme.palette.grey[800]
    return theme.palette.grey[50]
  }};

  ${({ theme }) =>
    createMediaStyles(() => ({
      phone: `padding: ${theme.spacing[3]} ${theme.spacing[0]} ${theme.spacing[7]}; background: ${
        getProjectName() === 'generic_v2' ? theme.palette.grey[800] : '#fff'
      }`,
      tablet: `padding: ${theme.spacing[6]} ${theme.spacing[6]} ${theme.spacing[4]}; background: ${
        getProjectName() === 'avito' || getProjectName() === 'tank'
          ? 'transparent'
          : getProjectName() === 'sollers'
          ? `rgba(41, 37, 35, 0.1)`
          : getProjectName() === 'generic_v2'
          ? `theme.palette.grey[800]`
          : theme.palette.grey[50]
      };`
    }))};
  border-radius: ${() => {
    if (getProjectName() === 'generic_v2') return '8px'
    return 0
  }};

  padding: ${() => {
    return getProjectName() === 'generic_v2' && '20px !important'
  }};

  @media screen and (max-width: 768px) {
    padding: ${() => {
      if (getProjectName() === 'generic_v2') return '10px!important'
      return `1.2rem 0rem 2.8rem`
    }};
  }
`

const Grid = styled.div`
  display: grid;
  grid-column-gap: ${({ theme }) => theme.spacing[6]};

  ${createMediaStyles(() => ({
    desktop: 'grid-template-columns: 1fr 1fr; grid-template-rows: auto 1fr',
    projectors: 'grid-template-columns: 2fr 1fr'
  }))}
`

const GalleryUnit = styled.div<{ isTab: boolean }>`
  min-width: 0;
  ${createMediaStyles(({ theme, isTab }) => ({
    tablet: `margin-bottom: ${getProjectName() !== 'haval' ? '37px' : isTab ? 0 : '30px'}`
  }))};

  @media screen and (max-width: 1024px) {
    margin-bottom: ${() => getProjectName() === 'generic_v2' && '0'};
  }
`

const TabsUnit = styled.div<{ sliderTabs?: boolean }>`
  min-width: 0;
  position: relative;

  ${({ theme, sliderTabs }) =>
    createMediaStyles(() => ({
      phone: `
        grid-row: 3 / 4;
        margin: 0 calc(${theme.grid.containerGutters} / -2); margin-bottom: ${sliderTabs ? '60px' : '0'}`,
      tablet: ` margin: 0 0 ${sliderTabs ? '60px' : '0'} 0;`,
      desktop: `grid-row: 2 / 3;`
    }))}
`

const DescriptionUnit = styled.div`
  ${({ theme }) =>
    createMediaStyles(() => ({
      phone: `margin-bottom: ${theme.spacing[1]};`,
      desktop: 'grid-row: 1 / 3; margin-bottom: 0;'
    }))};

  @media screen and (max-width: 768px) {
    margin: ${({ theme }) => {
      if (getProjectName() === 'generic_v2') {
        return `16px 0 ${theme.spacing[1]} 0`
      }
      return '0'
    }};
  }
`

const ButtonContainer = styled.div`
  width: ${({ theme }) => theme.spacing[44]};
  margin: ${({ theme }) => `${theme.spacing[10]} auto ${theme.spacing[0]}`};
`

const Wrapper = styled.div`
  padding: ${({ theme }) => `${theme.spacing[10]} ${theme.spacing[0]}`};
`

const Text = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing[2]};
  ${({ theme }) => theme.textStyles.p2};
  color: ${({ theme }) => theme.palette.grey[700]};
  line-height: 2;
  white-space: pre-line;
  padding: ${({ theme }) => `${theme.spacing[0]} ${theme.spacing[4]}`};
`

const Title = styled.h1`
  font-size: 1.4rem;
  font-family: ${({ theme }) => theme.fonts.body.family};
  font-weight: 700;
  text-transform: uppercase;
  padding: 0 ${({ theme }) => theme.spacing[4]} ${({ theme }) => theme.spacing[3]};
  margin: 0 0 ${({ theme }) => theme.spacing[3]} 0;
  border-bottom: 1px solid #d3d3d3;
  color: ${({ theme }) => {
    if (getProjectName() === 'generic_v2') return theme.palette.grey[100]
    return 'rgb(34, 34, 34)'
  }};
`

const SimilarCars = styled.div`
  margin-bottom: 82px;
  ${({ theme }) =>
    createMediaStyles(() => ({
      phone: `margin-bottom: 48px;`
    }))}
`

const SimilarCarsInner = styled.div`
  ${({ theme }) =>
    createMediaStyles(() => ({
      phone: `padding: 0 15px;`,
      tablet: 'padding: 0;'
    }))}
`

const Price = styled.div`
  margin-bottom: 5px;
  ${({ theme }) =>
    getProjectName() === 'avito'
      ? 'font-size: 2rem; font-family: "Manrope",-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto" ,"Helvetica Neue", Arial, sans-serif,  "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"'
      : theme.textStyles.h4};
  font-size: 25px;
  font-weight: 700;
`

const SimilarCarsSubtitle = styled.div`
  ${({ theme }) => theme.textStyles.p1};
  margin-bottom: ${({ theme }) => theme.spacing[7]};
  ${({ theme }) =>
    createMediaStyles(() => ({
      phone: `font-size: 14px;`
    }))}
`

const CostIndicator = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  position: relative;
  ${({ theme }) => theme.textStyles.p2};
  color: ${({ theme }) => theme.palette.grey[600]};
  ${({ theme }) =>
    createMediaStyles(() => ({
      phone: `grid-column-gap: 3px;`
    }))}
`

const CostIndicatorItem = styled.div`
  text-align: center;
  width: 100%;
  padding-top: 10px;
  position: relative;
  &:before {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    height: 4px;
    background: ${({ theme }) => theme.palette.grey[300]};
  }
  &:first-child {
    border-radius: 2px 0 0 2px;
  }
  &.active {
    color: ${(props) => props.color};
    &:before {
      background: ${(props) => props.color};
    }
    &:after {
      content: '';
      position: absolute;
      width: 10px;
      height: 10px;
      background: ${({ theme }) => theme.palette.black};
      top: -3px;
      left: calc(50% - 4px);
      border-radius: 50%;
      border: 2px solid ${({ theme }) => theme.palette.white};
      box-sizing: border-box;
    }
  }
  ${({ theme }) =>
    createMediaStyles(() => ({
      phone: `font-size: 12px;`
    }))}
`

const CreditLeasingButton = styled.div`
  ${({ theme }) =>
    createMediaStyles(() => ({
      phone: `padding: ${theme.spacing[3]} ${theme.spacing[0]} ${theme.spacing[7]};`,
      tablet: `padding: ${theme.spacing[6]} ${theme.spacing[6]} ${theme.spacing[4]};`
    }))}
  &.online-loan {
    opacity: 0.5;
  }

  @media screen and (min-width: 769px) and (max-width: 1023px) {
    display: ${() => getProjectName() === 'generic_v2' && `flex`};
    justify-content: ${() => getProjectName() === 'generic_v2' && `space-between`};
    padding-left: ${() => getProjectName() === 'generic_v2' && '20px'};
    padding-right: ${() => getProjectName() === 'generic_v2' && '20px'};

    div {
      max-width: ${() => getProjectName() === 'generic_v2' && `calc(50% - 5px)`};
      width: ${() => getProjectName() === 'generic_v2' && `100%`};

      a {
        max-width: none;
      }
    }

    a {
      max-width: ${() => getProjectName() === 'generic_v2' && `calc(50% - 5px)`};
    }
  }

  @media screen and (max-width: 768px) {
    padding-left: ${() => getProjectName() === 'generic_v2' && '10px'};
    padding-right: ${() => getProjectName() === 'generic_v2' && '10px'};
  }
`

const LargeViewIcon = styled.div`
  position: absolute;
  bottom: 20px;
  right: 20px;
  z-index: 1;
  cursor: pointer;
`

const SliderWrapper = styled.div`
  position: relative;

  @media screen and (max-width: 768px) {
    height: ${() => {
      if (getProjectName() === 'generic_v2') {
        return 'auto'
      }
      return '100%'
    }};
  }
`

const WrapIcons = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
  right: ${({ theme }) => theme.spacing[5]};
  bottom: ${({ theme }) => theme.spacing[5]};

  @media screen and (max-width: 768px) {
    position: ${() => {
      if (getProjectName() === 'generic_v2') {
        return 'static'
      }
      return 'absolute'
    }};
    margin: ${() => {
      if (getProjectName() === 'generic_v2') {
        return '4px 0 0 0'
      }
      return '0'
    }};
    justify-content: ${() => {
      if (getProjectName() === 'generic_v2') {
        return 'flex-start'
      }
      return 'center'
    }};
  }
`

const ButtonText = styled.div`
  font-weight: 700;
`

const InteractiveButton = styled.div`
  a {
    padding: 10px;
    border-radius: 8px;
    background: ${({ theme }) => {
      return theme.palette.grey[800]
    }};
    height: auto;

    &:hover {
      background: ${({ theme }) => {
        return theme.palette.grey[900]
      }};
    }

    @media screen and (max-width: 768px) {
      width: ${() => {
        if (getProjectName() === 'generic_v2') {
          return '100%'
        }
        return 'auto'
      }};
    }
  }

  div {
    font-size: 14px;
    font-weight: 400;
    color: ${({ theme }) => {
      return theme.palette.grey[100]
    }};
  }

  @media screen and (max-width: 768px) {
    width: ${() => {
      if (getProjectName() === 'generic_v2') {
        return '100%'
      }
      return 'auto'
    }};
  }
`

const VideoButton = styled.div`
  border-radius: ${({ theme }) => {
    if (getProjectName() === 'generic_v2') return '8px'
    return '9px'
  }};
  display: flex;
  justify-content: center;
  align-items: center;
  width: ${({ theme }) => {
    if (getProjectName() === 'generic_v2') return '56px'
    return theme.spacing[12]
  }};
  height: ${({ theme }) => {
    if (getProjectName() === 'generic_v2') return '39px'
    return theme.spacing[9]
  }};
  color: ${({ theme }) => theme.palette.brand.primary};
  cursor: pointer;
  background: ${({ theme }) => {
    if (getProjectName() === 'tank') return 'none'
    if (getProjectName() === 'generic_v2') return theme.palette.grey[800]
    return 'hsla(0, 0%, 100%, 0.5)'
  }};
  backdrop-filter: blur(2px);
  box-shadow: ${() => {
    if (getProjectName() === 'tank') return
    if (getProjectName() === 'generic_v2') return
    return '0 0 5px 2px hsla(0, 0%, 100%, 0.3)'
  }};
  transition: 0.2s linear;
  margin-left: ${({ theme }) => {
    if (getProjectName() === 'generic_v2') {
      return '4px'
    }
    return theme.spacing[4]
  }};

  &:hover {
    background: ${({ theme }) => {
      if (getProjectName() === 'tank') return 'none'
      if (getProjectName() === 'generic_v2') return theme.palette.grey[900]
      return 'hsla(0, 0%, 100%, 0.7)'
    }};
    box-shadow: ${() => {
      if (getProjectName() === 'tank') return 'none'
      if (getProjectName() === 'generic_v2') return 'none'
      return '0 0 4px 1px hsla(0, 0%, 100%, 0.5)'
    }};

    path {
      transition: 0.2s linear;
      opacity: 0.7;
    }
  }

  svg {
    color: ${() => {
      if (getProjectName() === 'generic_v2') {
        return '#f9f9f9'
      }
      return null
    }}
`

const ColorsWrapper = styled.div<{ showColorsInDescriptionOnly: boolean }>`
  position: absolute;
  bottom: ${({ theme }) => theme.spacing[5]};
  left: ${() => {
    if (getProjectName() === 'generic_v2') return 0
    return '50%'
  }};
  display: flex;

  justify-content: center;
  align-items: center;
  padding: ${({ theme }) => {
    if (getProjectName() === 'generic_v2') return '10px'
    return `${theme.spacing[2]} ${theme.spacing[4]}`
  }};
  border-radius: ${() => {
    return '2px'
  }};
  background: ${({ theme }) => {
    if (getProjectName() === 'tank') return 'none'
    if (getProjectName() === 'generic_v2') return `none`
    return 'hsla(0, 0%, 100%, 0.5)'
  }};
  backdrop-filter: blur(2px);
  transform: ${() => {
    if (getProjectName() === 'generic_v2') return 'translateX(0)'
    return 'translateX(-50%)'
  }};

  @media screen and (max-width: 768px) {
    position: ${() => {
      if (getProjectName() === 'generic_v2') {
        return 'static'
      }
      return 'absolute'
    }};
    margin: ${() => {
      return '0'
    }};
    justify-content: ${() => {
      if (getProjectName() === 'generic_v2') {
        return 'flex-start'
      }
      return 'center'
    }};
  }

  @media screen and (width: 768px) {
    position: absolute;
  }

  ${({ showColorsInDescriptionOnly }) =>
    showColorsInDescriptionOnly
      ? createMediaStyles(() => {
          return {
            phone: 'display: none;',
            tablet: 'display: none;',
            desktop: 'display: none;',
            projectors: 'display: none;'
          }
        })
      : createMediaStyles(() => {
          if (getProjectName() === 'generic_v2') {
            return {
              phone: 'display: flex;',
              tablet: 'display: flex;',
              desktop: 'display: flex;',
              projectors: 'display: flex;'
            }
          }
          return {
            phone: 'display: flex;',
            tablet: 'display: none;',
            desktop: 'display: flex;',
            projectors: 'display: none;'
          }
        })}

  ${({ theme }) =>
    getProjectName() === 'generic_v2' &&
    `
        @media screen and (max-width: 767px) {
          flex-direction: column
        }
      `}
`

const ColorWrapper = styled.div`
  display: flex;
  align-items: center;

  &:not(last-child) {
    margin-right: ${({ theme }) => theme.spacing[4]};

    ${({ theme }) =>
      getProjectName() === 'generic_v2' &&
      `
        margin-right: 4px;
        
        @media screen and (max-width: 767px) {
          margin-bottom: 4px;
        }
      `}
  }

  ${({ theme }) =>
    getProjectName() === 'generic_v2' &&
    `
        background: ${theme.palette.grey[800]};
        padding: 10px;
        border-radius: 8px;
        
        @media screen and (max-width: 767px) {
          width: 100%;
        }
      `}
`

const ColorInner = styled.div`
  display: flex;
  align-items: center;
`

const SliderColor = styled.div<{ color: string; secondaryColor?: string }>`
  flex-shrink: 0;
  width: ${({ theme }) => {
    if (getProjectName() === 'generic_v2') {
      return '12px'
    }
    return theme.spacing[6]
  }};
  height: ${({ theme }) => {
    if (getProjectName() === 'generic_v2') {
      return '12px'
    }
    return theme.spacing[6]
  }};
  margin-right: ${({ theme }) => {
    if (getProjectName() === 'generic_v2') {
      return '10px'
    }
    return theme.spacing[4]
  }};
  border-radius: 50%;
  background: ${({ color, secondaryColor }) =>
    secondaryColor ? `linear-gradient(90deg, ${color} 49%, ${secondaryColor} 51%)` : color};
`

const ColorHeading = styled.div`
  text-transform: ${() => {
    if (getProjectName() === 'generic_v2') return 'none'
    return 'uppercase'
  }};
  ${({ theme }) => theme.textStyles.p4};
  color: ${({ theme }) => {
    if (getProjectName() === 'tank') return '#7C7F7F'
    if (getProjectName() === 'generic_v2') return `${theme.palette.grey[600]}`
    return theme.palette.grey[700]
  }};

  font-size: ${({ theme }) => {
    if (getProjectName() === 'generic_v2') return `14px`
    return '10px'
  }};
  margin-bottom: ${({ theme }) => {
    if (getProjectName() === 'generic_v2') return `3px`
    return 0
  }};
`

const ColorName = styled.div`
  ${({ theme }) => theme.textStyles.p3};
  color: ${({ theme }) => {
    if (getProjectName() === 'tank') return '#7C7F7F'
    if (getProjectName() === 'sollers') return `${theme.palette.grey[50]}`
    if (getProjectName() === 'generic_v2') return `${theme.palette.grey[100]}`
    return theme.palette.grey[900]
  }};

  font-size: ${({ theme }) => {
    if (getProjectName() === 'generic_v2') return `14px`
    return '12px'
  }};
`

const LabelsWrapper = styled.div`
  position: absolute;
  top: ${({ theme }) => theme.spacing[5]};
  left: ${({ theme }) => theme.spacing[5]};
  display: flex;
  flex-wrap: wrap;
`

const SliderLabelWrapper = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing[3]};

  &:not(:last-child) {
    margin-right: ${({ theme }) => theme.spacing[1]};
  }
`

const Inner = styled.div<{ bg: string }>`
  background-color: ${({ theme, bg }) => {
    if (bg === 'gray10') return '#f8f8f8'
    if (bg === 'gray2') return '#252c2c'
    if (bg === 'black') return '#0f0f0f'
    if (bg === 'none') return
    return '#ffffff'
  }};
`
